import type {ReactElement} from 'react';
import {Text, TextSize} from '@Components/text';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {QuestionInputType, QUESTION_TYPE_DESCRIPTORS} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.types';
import {requiresOptions} from '@Components/event-wizard/event-wizard-ticketing/event-wizard-ticketing.helper';
import {TicketingRequiredPill} from '@Components/event-wizard/event-wizard-ticketing/components/ticketing-required-pill/ticketing-required-pill';
import {TicketingPill} from '@Components/event-wizard/event-wizard-ticketing/components/ticketing-pill/ticketing-pill';
import {TicketingQuestionBox} from '@Components/event-wizard/event-wizard-ticketing/components/ticketing-question-box/ticketing-question-box';
import type {TicketingQuestionCardProps} from './ticketing-question-card.types';
import styles from './ticketing-question-card.module.scss';

export function TicketingQuestionCard({question, displayNumber, onEdit, onDelete, dragHandleClass}: TicketingQuestionCardProps): ReactElement {
  const descriptor = QUESTION_TYPE_DESCRIPTORS[question.type];
  const optionGlyph = question.type === QuestionInputType.SINGLE_SELECT ? 'icon-circle' : 'icon-checkbox-unchecked';

  const getRequiredPill = (): ReactElement | null => {
    if (!question.required) {
      return null;
    }
    return <TicketingRequiredPill />;
  };

  const getOptionsPreview = (): ReactElement | null => {
    if (!requiresOptions(question.type)) {
      return null;
    }

    return (
      <div className="gap-2 flex-column-align-start spacing-m-t-3 _full-width">
        {question.options.map((option, index) => {
          return (
            <div className={`gap-2 flex-row-align-center ${styles.optionRow}`} key={`${index}-${option}`}>
              <Icon className="ct-neutral-secondary flex-shrink-0" icon={optionGlyph} size={IconSize.SIZE_ICON_16} />
              <Text className="ct-neutral-primary" val={option} size={TextSize.MD} />
            </div>
          );
        })}
      </div>
    );
  };

  const getQuestionText = (): ReactElement | null => {
    // Phone questions have a fixed 'Phone number' title already shown by the type pill, so hide the name.
    if (question.type === QuestionInputType.PHONE) {
      return null;
    }
    return <Text className="ct-neutral-primary" val={question.text} size={TextSize.MD} />;
  };

  return (
    <TicketingQuestionBox
      displayNumber={displayNumber}
      dragHandleClass={dragHandleClass}
      actions={{onEdit, onDelete}}
      onCardClick={onEdit}
      pills={
        <>
          <TicketingPill icon={descriptor.icon} label={window.i18next.t(descriptor.labelKey)} />
          {getRequiredPill()}
        </>
      }
    >
      {getQuestionText()}
      {getOptionsPreview()}
    </TicketingQuestionBox>
  );
}
